/**
 * DocumentItem Component
 * Individual document row with metadata and actions
 */

import type { ProspectDocument } from '../../../../../types/prospect-media';
import {
  getDocumentIcon,
  getDocumentIconColor,
  getDocumentTypeLabel,
  formatFileSize,
  formatDocumentDate,
} from '../../../../../utils/document-utils';
import { ChevronRightIcon } from '../../../../../assets/ChevronRightIcon';
import './DocumentItem.css';

interface DocumentItemProps {
  document: ProspectDocument;
  onDocumentClick: (document: ProspectDocument) => void;
  onDeleteClick?: (documentId: string) => void;
  isDownloading?: boolean;
}

export function DocumentItem({
  document,
  onDocumentClick,
  isDownloading = false,
}: DocumentItemProps) {
  const icon = getDocumentIcon(document.contentType, document.fileExtension);
  const iconColor = getDocumentIconColor(document.contentType, document.fileExtension);
  const typeLabel = getDocumentTypeLabel(document.contentType, document.fileExtension);
  const fileSize = formatFileSize(document.fileSizeBytes);
  const date = formatDocumentDate(document.createdAt);

  // Display title or generate default
  const displayTitle = document.title || `Document${document.fileExtension}`;

  const handleClick = () => {
    if (!isDownloading) {
      onDocumentClick(document);
    }
  };

  return (
    <div
      className={`document-item ${isDownloading ? 'document-item--downloading' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div
        className="document-item__icon"
        style={{
          backgroundColor: `${iconColor}15`,
          color: iconColor
        }}
      >
        {icon}
      </div>

      <div className="document-item__content">
        <div className="document-item__header">
          <span className="document-item__title">{displayTitle}</span>
        </div>

        <div className="document-item__metadata">
          <span className="document-item__type">{typeLabel}</span>
          <span className="document-item__separator">•</span>
          <span className="document-item__size">{fileSize}</span>
          <span className="document-item__separator">•</span>
          <span className="document-item__date">{date}</span>
        </div>
      </div>

      {isDownloading ? (
        <div className="document-item__loading">Loading...</div>
      ) : (
        <ChevronRightIcon className="document-item__chevron" />
      )}
    </div>
  );
}
